import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useModal } from 'react-modal-hook'
import Modal from '../../components/Modal/Modal'
import NeoButton from '../../components/NeoButton/NeoButton'
import {
  getUserDetailsSelector,
  getCompanyDetailsSelector,
  profileImageDetailsSelector,
} from '../../store/selectors/app.selector'
import AuthService from '../../services/auth.service'
import LayoutWithOutAccountCard from '../Layout/layout-without-card'
import DeleteIcon from 'custom/assets/icons/delete.svg'
import UploadIcon from 'custom/assets/icons/upload.svg'
import moment from 'moment'
import Loading from 'custom/assets/loaders/Loading'
import { setProfileImageDetails } from '../../store/actions/app.actions'
import UserImageIcon from 'custom/assets/dashboard/dummy-user.png'
import './Profile.css'
import { useTranslation } from 'react-i18next'
import { BUTTON_KEYS, MODAL_KEYS, PROFILE_KEYS } from '../../utils/translation-constants'

export default function Profile() {
  const dispatch = useDispatch()
  const userDetails = useSelector(getUserDetailsSelector)
  const companyDetails = useSelector(getCompanyDetailsSelector)
  const image = useSelector(profileImageDetailsSelector)
  const [stage, setStage] = useState(0)
  const inputFile = useRef(null)
  const [imgLocation, setImgLocation] = useState('')
  const [imageData, setImageData] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6>{message}</h6>
          <div className='modal-btn-style'>
            <NeoButton
              tabIndex='1'
              mode='newDesign'
              type='button'
              text={t(BUTTON_KEYS.OK)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  const [deleteModal, hideDeleteModal] = useModal(
    () => (
      <Modal>
        <h6>{t(MODAL_KEYS.IMAGE_DELETE_MESSAGE)}</h6>
        <div className='row justify-content-center align-items-center'>
          <div className='mt-4 mx-3 col-5'>
            <NeoButton tabIndex='1' type='button' mode='newDesign' text={t(BUTTON_KEYS.YES)} onClick={deleteImage} />
          </div>
          <div className='mt-4 mx-1 col-5'>
            <button
              tabIndex={'2'}
              type='button'
              onClick={hideDeleteModal}
              className='btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
              {t(BUTTON_KEYS.NO)}
            </button>
          </div>
        </div>
      </Modal>
    ),
    [imageData],
  )

  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderLoading()
      case 1:
        return renderProfile()

      default:
        return renderLoading()
    }
  }

  const updateProfileImage = useCallback(
    async (imageData) => {
      setLoading(true)
      try {
        const response = await AuthService.uploadProfileImage(imageData)
        if (response.status === 200) {
          //dispatch(setProfileImageDetails(imageData))
          setMessage(response.data['message'])
          setLoading(false)
          showModal()
        }
      } catch (err) {
        setMessage(err.message)
        setLoading(false)
      }
    },
    [inputFile],
  )

  const deleteImage = useCallback(async () => {
    try {
      setLoading(true)
      hideDeleteModal()
      const response = await AuthService.deleteProfileImage()
      if (response.status === 200) {
        //dispatch(setProfileImageDetails(UserImageIcon))
        setLoading(false)
        setMessage(response.data['message'])
        showModal()
      }
    } catch (err) {
      setLoading(false)
      setMessage(err.message)
    }
  }, [showModal, hideDeleteModal])

  const handleFileRead = useCallback(
    async (e) => {
      const content = e.target.result
      await updateProfileImage(content.toString().split(',')[1])
    },
    [updateProfileImage],
  )

  useEffect(() => {
    const fileReader = new FileReader()
    fileReader.onloadend = handleFileRead
    imgLocation === '' ? console.log('') : fileReader.readAsDataURL(imgLocation)
  }, [imgLocation, handleFileRead])

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderProfile() {
    return (
      <div id='Profile' className='container-fluid h-100'>
        <div>
          <div className='mt-5 ml-4 d-flex justify-content-center'>
            <div className='profile-picture rounded-circle'>
              {loading ? (
                renderLoading()
              ) : (
                <img
                  className='profile-picture rounded-circle'
                  src={image === null ? UserImageIcon : `data:image/png;base64,${image}`}
                  alt=' Profile'
                />
              )}{' '}
            </div>
            {image !== null && (
              <button
                tabIndex={'1'}
                type='button'
                onClick={deleteModal}
                className='btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
                <img src={DeleteIcon} />
              </button>
            )}
          </div>
          <div className='text-center mt-3'>
            <button
              tabIndex={'2'}
              type='button'
              ref={inputFile}
              onClick={() => {
                inputFile.current.click()
              }}
              className='btn btn-sm btn-link small new-design-warning-text-color font-weight-600'>
              <img src={UploadIcon} className='mr-2' />
              {t(PROFILE_KEYS.IMAGE_UPLOAD)}
            </button>
          </div>
          <input
            type='file'
            id='file'
            accept='image/png, image/jpeg, image/jpg'
            ref={inputFile}
            style={{ display: 'none' }}
            onChange={(e) => {
              var size = e.target.files[0].size / 1000
              if (size > 100) {
                setMessage('Image Size too large. Please Upload images less than 100Kb')
                e.target.value = null
                showModal()
                return
              }
              setImgLocation(e.target.files[0])
              e.target.value = null
            }}
          />
          <div className='row my-5 text-center'>
            <div className='col-11 mx-auto'>
              <h4 className='mb-4 font-weight-bold'>
                {userDetails.firstName} {userDetails.middleName} {userDetails.lastName}
              </h4>

              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                  {t(PROFILE_KEYS.USER_NAME)}
                </div>
                <div className='col-8 font-weight-bold  text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.userName}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                  {t(PROFILE_KEYS.COMPANY_NAME)}
                </div>
                <div className='col-8 font-weight-bold  text-left' style={{ fontSize: '14px' }}>
                  {companyDetails?.companyName}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                  {t(PROFILE_KEYS.COMPANY_BRANCH)}
                </div>
                <div className='col-8 font-weight-bold  text-left' style={{ fontSize: '14px' }}>
                  {companyDetails?.branch}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                  {t(PROFILE_KEYS.DOB)}
                </div>
                <div className='col-8 font-weight-bold text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.customerDob}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                  {t(PROFILE_KEYS.MOBILE_NO)}
                </div>
                <div className='col-8 font-weight-bold text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.mobileNo}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                {t(PROFILE_KEYS.GENDER)}
                </div>
                <div className='col-8 font-weight-bold text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.gender}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                {t(PROFILE_KEYS.EMAIL)}
                </div>
                <div className='col-8 font-weight-bold  text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.emailId}
                </div>
              </div>
              <div className='row my-2 py-2'>
                <div className='col-4 text-right border-right' style={{ fontSize: '10px' }}>
                {t(PROFILE_KEYS.ADDRESS)}
                </div>
                <div className='col-8 font-weight-bold  text-left' style={{ fontSize: '14px' }}>
                  {userDetails?.homeAddress}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <LayoutWithOutAccountCard
      breadCumKey='PROFILE'
      keyPath={'PROFILE_SETTINGS'}
      showEmptySpace={true}>
      <div className='new-design-form-style' style={{ padding: '20px 36px !important' }}>
        <div className='heading-style'>{t(PROFILE_KEYS.HEADER)}</div> {renderProfile()}
      </div>
      <div className='mb-5' />
    </LayoutWithOutAccountCard>
  )
}
